import {
  CheckCircle2,
  ClipboardList,
  Rocket,
} from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import CtaBand from "@/components/CtaBand";

const TIMELINE = [
  {
    when: "Day 0",
    title: "You fill out the form, I build a preview.",
    detail:
      "The form takes about five minutes. The preview is a real website with your business on it — not a mockup.",
  },
  {
    when: "Day 1–2",
    title: "I send a short video walkthrough.",
    detail: "Watch it whenever it's convenient. No sales pitch, just your site.",
  },
  {
    when: "Then",
    title: "A 15-minute call.",
    detail: "Keep what you like, change what you don't.",
  },
  {
    when: "Then",
    title: "Under two weeks to launch.",
    detail:
      "I handle the technical parts. You approve the design, and we go live.",
  },
];

const NEEDS = [
  "Your logo, if you have one",
  "Job photos",
  "Your service list",
  "Your service area",
  "Access to your domain or Google Business Profile",
];

const AFTER_LAUNCH = [
  "Hosting and updates are handled",
  "You text me changes",
  "Your automation is monitored",
  "You get a monthly summary of leads",
];

export default function HowItWorks() {
  return (
    <>
      <Seo
        title="How Web Pilot Builds Your Site | Web Pilot"
        description="See your new website before you pay for it. Web Pilot builds a free preview, sends a video walkthrough, and launches home service sites in under two weeks."
      />

      <section className="bg-navy py-16 sm:py-20" data-testid="hiw-hero">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-skyblue">
              How it works
            </p>
            <h1
              data-testid="hiw-heading"
              className="mt-3 max-w-3xl font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl"
            >
              See it before you pay for it.
            </h1>
            <p className="mt-5 max-w-2xl text-base text-slate-300 sm:text-lg">
              No proposals. No contracts up front. You see the actual website,
              then decide.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-offwhite py-20 sm:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
              From Day 0 to launch
            </h2>
          </Reveal>
          <ol className="relative mt-10 space-y-10 border-l-2 border-dashed border-skyblue/40 pl-8 sm:pl-10">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.08}>
                <li className="relative" data-testid={`hiw-step-${i + 1}`}>
                  <span
                    className="absolute -left-[2.55rem] top-1.5 h-3 w-3 rounded-full bg-skyblue ring-4 ring-skyblue-soft sm:-left-[3.05rem]"
                    aria-hidden="true"
                  />
                  <p className="font-display text-sm font-bold uppercase tracking-[0.14em] text-skyblue">
                    {t.when}
                  </p>
                  <h3 className="mt-2 font-display text-xl font-bold text-navy sm:text-2xl">
                    {t.title}
                  </h3>
                  <p className="mt-2 leading-relaxed text-slate-600">{t.detail}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* What I need from you */}
      <section className="bg-white py-20 sm:py-24">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <div className="flex h-full flex-col rounded-xl border border-slate-200/80 bg-offwhite p-6 shadow-sm sm:p-8">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-white shadow-sm">
                <ClipboardList className="h-6 w-6 text-skyblue" aria-hidden="true" />
              </div>
              <h2 className="mt-6 font-display text-2xl font-bold tracking-tight text-ink">
                What I need from you
              </h2>
              <ul className="mt-5 space-y-3.5">
                {NEEDS.map((n) => (
                  <li
                    key={n}
                    className="flex items-start gap-3 leading-relaxed text-slate-600"
                  >
                    <CheckCircle2
                      className="mt-0.5 h-5 w-5 shrink-0 text-skyblue"
                      aria-hidden="true"
                    />
                    {n}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm text-slate-500">
                None of this has to be perfect. Send what you have.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="flex h-full flex-col rounded-xl border border-slate-200/80 bg-offwhite p-6 shadow-sm sm:p-8">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-white shadow-sm">
                <Rocket className="h-6 w-6 text-skyblue" aria-hidden="true" />
              </div>
              <h2 className="mt-6 font-display text-2xl font-bold tracking-tight text-ink">
                What happens after launch
              </h2>
              <ul className="mt-5 space-y-3.5">
                {AFTER_LAUNCH.map((n) => (
                  <li
                    key={n}
                    className="flex items-start gap-3 leading-relaxed text-slate-600"
                  >
                    <CheckCircle2
                      className="mt-0.5 h-5 w-5 shrink-0 text-skyblue"
                      aria-hidden="true"
                    />
                    {n}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm text-slate-500">
                You run the business. I keep the website and automation running.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
